import {
  describe,
  fromLocalInputValue,
  Input,
  toast,
  toLocalInputValue,
} from '@servicetim/ui/components';
import { useId, useState } from 'react';
import { formatDateTime, relativeTime } from './format';
import { RailCard, RailRow } from './rail-card';
import { useTicketContext } from './ticket-context';

function When({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-fg-muted">—</span>;
  return (
    <time dateTime={iso} title={formatDateTime(iso)}>
      {formatDateTime(iso)}
      <span className="ml-1 text-fg-muted text-xs">({relativeTime(iso)})</span>
    </time>
  );
}

/** Created / updated / due (editable) / resolved / acknowledged. */
export function DatesCard() {
  const { ticket, patch, patching } = useTicketContext();
  const dueId = useId();
  const [due, setDue] = useState(() => toLocalInputValue(ticket.dueAt));
  const [editingDue, setEditingDue] = useState(false);

  const saveDue = async (local: string) => {
    const iso = fromLocalInputValue(local) ?? null;
    if (iso === ticket.dueAt) return;
    try {
      const saved = await patch({ dueAt: iso });
      if (saved) toast.success(iso ? 'Due date saved' : 'Due date cleared');
    } catch (e) {
      toast.error(describe(e).title);
      setDue(toLocalInputValue(ticket.dueAt));
    }
  };

  return (
    <RailCard title="Dates">
      <dl className="space-y-3">
        <RailRow label="Created">
          <When iso={ticket.createdAt} />
        </RailRow>
        <RailRow label="Updated">
          <When iso={ticket.updatedAt} />
        </RailRow>
        <RailRow label="Due">
          {ticket.canEdit ? (
            <Input
              id={dueId}
              aria-label="Due"
              type="datetime-local"
              value={editingDue ? due : toLocalInputValue(ticket.dueAt)}
              onFocus={() => {
                setDue(toLocalInputValue(ticket.dueAt));
                setEditingDue(true);
              }}
              onChange={(e) => setDue(e.target.value)}
              onBlur={() => {
                setEditingDue(false);
                void saveDue(due);
              }}
              disabled={patching}
              className="h-8 w-auto"
            />
          ) : (
            <When iso={ticket.dueAt} />
          )}
        </RailRow>
        <RailRow label="Resolved">
          <When iso={ticket.resolvedAt} />
        </RailRow>
        <RailRow label="Acknowledged">
          {ticket.acknowledgedAt ? (
            <span>
              <When iso={ticket.acknowledgedAt} />
              {ticket.display.acknowledged_by ? (
                <span className="block text-fg-muted text-xs">
                  by {ticket.display.acknowledged_by}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-fg-muted">Not yet</span>
          )}
        </RailRow>
      </dl>
    </RailCard>
  );
}
